import { groupBy } from "lodash-es";
import type { EventHoursOverride, Occurrence, Hours } from "../types";
import {
  dayRange,
  getDayOfWeek,
  isSameDate,
  hours,
  minutes,
  ensureDate,
  getStartOfDay
} from "../support/dates";
import { DateTime } from "luxon";

function timeDiff(a: Date, b: Date): number {
  return a.valueOf() - b.valueOf();
}

const openingSoonThreshold = hours(1);
export function isOpeningSoon(occurrence: Occurrence, now: Date): boolean {
  const diff = timeDiff(occurrence.from, now);

  if (diff <= 0) {
    return false;
  }

  return diff <= openingSoonThreshold;
}

const closingSoonThreshold = minutes(30);
export function closesWithinThirtyMinutes(occurrence: Occurrence, now: Date): boolean {
  const diff = timeDiff(occurrence.until, now);

  if (diff <= 0) {
    return false;
  }

  return diff <= closingSoonThreshold;
}

export function opensToday(occurrence: Occurrence, now: Date, timeZone: string): boolean {
  const diff = timeDiff(occurrence.from, now);

  if (diff <= 0) {
    return false;
  }

  return isSameDate(occurrence.from, now, timeZone);
}

export function isOpenNow(occurrence: Occurrence, now: Date): boolean {
  const startDiff = timeDiff(occurrence.from, now);
  const endDiff = timeDiff(occurrence.until, now);

  return startDiff <= 0 && endDiff > 0;
}

export function onDay<Type extends Occurrence>(
  schedule: Array<Type>,
  date: Date,
  timeZone: string
): Array<Type> {
  return schedule.filter((hours) => {
    return isSameDate(hours.from, date, timeZone);
  });
}

type ScheduleGroups<Type extends Occurrence> = {
  [key: string]: Array<Type>;
};

export function getScheduleGroups<Type extends Occurrence>(
  schedule: Array<Type>,
  time: Date,
  timeZone: string
): ScheduleGroups<Type> {
  return groupBy(schedule, (occurrence) => {
    if (closesWithinThirtyMinutes(occurrence, time)) {
      return "closingSoon";
    }

    if (isOpeningSoon(occurrence, time)) {
      return "openingSoon";
    }

    if (isOpenNow(occurrence, time)) {
      return "open";
    }

    if (opensToday(occurrence, time, timeZone)) {
      return "opening";
    }

    // If not open, it's probably closed
    return "closed";
  });
}

export function parseTime(time: string): [number, number] {
  const match = time.match(/(\d{2}):(\d{2})/);

  if (match) {
    const [_, hours, minutes] = match;
    return [parseInt(hours), parseInt(minutes)];
  }

  // TODO - this isn't really right. We should just return this value from the API;
  return [0, 0];
}

/**
 * Returns true when the occurence is within the hours range
 * for the given dates.
 */
function isWithinHoursDates<Type extends Occurrence>(hours: Hours, occurrence: Type) {
  if (hours.startDate) {
    // hours.startDate is ISO date string
    if (ensureDate(hours.startDate) > occurrence.from) {
      return false;
    }
  }

  if (hours.endDate) {
    // hours.endDate is ISO date string
    if (ensureDate(hours.endDate) < occurrence.until) {
      return false;
    }
  }

  return true;
}

export function reify<Type extends Occurrence>(
  hours: Hours,
  rangeStartDate: Date,
  repeat: number,
  timeZone: string
): Array<Type> {
  const givenDays = new Set(hours.weekdays);

  // Using start of day for start date ensures consistent hours shifting
  const range = dayRange(getStartOfDay(rangeStartDate, timeZone), repeat);

  const includedDays = range.filter((date) => {
    return givenDays.has(getDayOfWeek(date, timeZone));
  });

  return includedDays
    .flatMap(function (day) {
      const overrides = (hours.overrides || []).filter((override: EventHoursOverride) => {
        return ensureDate(override.date).valueOf() == day.valueOf();
      });

      const startTime = overrides.reduce(function (
        startTime: string,
        override: EventHoursOverride
      ) {
        return override.startTime || startTime;
      }, hours.startTime);

      const endTime = overrides.reduce(function (endTime: string, override: EventHoursOverride) {
        return override.endTime || endTime;
      }, hours.endTime);

      const cancelled = overrides.reduce(function (
        cancelled: boolean,
        override: EventHoursOverride
      ) {
        return override.cancelled || cancelled;
      }, false);

      if (cancelled) {
        return [];
      }

      const [startHours, startMinutes] = parseTime(startTime);
      const [endHours, endMinutes] = parseTime(endTime);

      const occurence: unknown = {
        ...hours,
        from: DateTime.fromJSDate(day, { zone: timeZone })
          .set({ hour: startHours, minute: startMinutes })
          .toJSDate(),
        until: DateTime.fromJSDate(day, { zone: timeZone })
          .set({ hour: endHours, minute: endMinutes })
          .toJSDate()
      };

      // @ts-expect-error merge cleanup
      delete occurence.startTime;
      // @ts-expect-error merge cleanup
      delete occurence.endTime;
      // @ts-expect-error merge cleanup
      delete occurence.startDate;
      // @ts-expect-error merge cleanup
      delete occurence.endDate;
      // @ts-expect-error merge cleanup
      delete occurence.weekdays;
      // @ts-expect-error merge cleanup
      delete occurence.overrides;

      return [occurence as Type];
    })
    .filter((occurrence: Type) => {
      return isWithinHoursDates<Type>(hours, occurrence);
    });
}
